/**
 * 💀 Affiliate Skeleton Components
 * Reusable loading state placeholders for the Affiliate Portal
 */

import React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// ============================================
// BASE SKELETON
// ============================================

interface SkeletonProps {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={cn('animate-pulse bg-gray-200 rounded', className)} />
);

// ============================================
// KPI CARD SKELETON
// ============================================

export const SkeletonKPICard: React.FC<{ className?: string }> = ({ className }) => (
  <Card className={cn('p-5', className)}>
    <div className="flex items-center gap-4">
      <Skeleton className="w-12 h-12 rounded-xl" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-7 w-28" />
      </div>
      <Skeleton className="h-5 w-14 rounded-full" />
    </div>
  </Card>
);

// ============================================
// STAT CARD SKELETON (simpler variant)
// ============================================

export const SkeletonStatCard: React.FC<{ className?: string }> = ({ className }) => (
  <Card className={cn('p-4', className)}>
    <div className="flex items-center gap-3">
      <Skeleton className="w-10 h-10 rounded-lg" />
      <div className="space-y-1.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-6 w-20" />
      </div>
    </div>
  </Card>
);

// ============================================
// CHART SKELETON
// ============================================

export const SkeletonChart: React.FC<{ className?: string; height?: number }> = ({ 
  className,
  height = 280 
}) => (
  <Card className={cn('p-5', className)}>
    <div className="flex items-center justify-between mb-4">
      <div className="space-y-1">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
      <Skeleton className="h-8 w-24 rounded-md" />
    </div>
    <div style={{ height }}>
      <Skeleton className="w-full h-full rounded-lg" />
    </div>
    <div className="flex justify-center gap-6 mt-4">
      <div className="flex items-center gap-2">
        <Skeleton className="w-3 h-3 rounded-full" />
        <Skeleton className="h-3 w-16" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="w-3 h-3 rounded-full" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  </Card>
);

// ============================================
// TABLE ROW SKELETON
// ============================================

export const SkeletonTableRow: React.FC<{ columns?: number }> = ({ columns = 5 }) => (
  <tr className="border-b border-gray-100">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="py-4 px-4">
        <Skeleton className={cn(
          'h-4',
          i === 0 && 'w-20',
          i === 1 && 'w-32',
          i === columns - 2 && 'w-16 rounded-full',
          i === columns - 1 && 'w-20 ml-auto',
          ![0, 1, columns - 2, columns - 1].includes(i) && 'w-24'
        )} />
      </td>
    ))}
  </tr>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number; className?: string }> = ({ 
  rows = 5, 
  columns = 5,
  className 
}) => (
  <Card className={cn('overflow-hidden', className)}>
    <div className="px-5 py-4 border-b border-gray-100">
      <Skeleton className="h-5 w-32" />
    </div>
    <table className="w-full">
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonTableRow key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </Card>
);

// ============================================
// LIST ITEM SKELETON
// ============================================

export const SkeletonListItem: React.FC = () => (
  <div className="flex items-center gap-3 py-3">
    <Skeleton className="w-9 h-9 rounded-full" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
    <Skeleton className="h-4 w-16" />
  </div>
);

export const SkeletonList: React.FC<{ items?: number; className?: string }> = ({ 
  items = 5,
  className 
}) => (
  <Card className={cn('p-5', className)}>
    <Skeleton className="h-5 w-28 mb-4" />
    <div className="divide-y divide-gray-100">
      {Array.from({ length: items }).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
    </div>
  </Card>
);

// ============================================
// RESTAURANT CARD SKELETON
// ============================================

export const SkeletonRestaurantCard: React.FC<{ className?: string }> = ({ className }) => (
  <Card className={cn('p-5', className)}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <Skeleton className="w-12 h-12 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
      <Skeleton className="h-5 w-14 rounded-full" />
    </div>
    <div className="grid grid-cols-3 gap-4 py-4 border-t border-b border-gray-100">
      {[1, 2, 3].map(i => (
        <div key={i} className="space-y-1">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-5 w-14" />
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between mt-4">
      <Skeleton className="h-3 w-32" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-20 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  </Card>
);

// ============================================
// PROGRESS CARD SKELETON
// ============================================

export const SkeletonProgressCard: React.FC<{ className?: string }> = ({ className }) => (
  <Card className={cn('p-5', className)}>
    <div className="flex items-center gap-4 mb-4">
      <Skeleton className="w-14 h-14 rounded-2xl" />
      <div className="space-y-1.5">
        <Skeleton className="h-6 w-36" />
        <Skeleton className="h-4 w-48" />
      </div>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-3 w-full rounded-full" />
      <Skeleton className="h-3 w-40 mx-auto" />
    </div>
  </Card>
);

export default {
  Skeleton,
  SkeletonKPICard,
  SkeletonStatCard,
  SkeletonChart,
  SkeletonTable,
  SkeletonTableRow,
  SkeletonList,
  SkeletonListItem,
  SkeletonRestaurantCard,
  SkeletonProgressCard,
};
